import { PayloadAction } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { usePrevious } from 'shared/hooks';
import { useAppSelector } from 'shared/store';
import { RealtimeState } from 'shared/store/realtime-slice';
import { callWanted } from 'shared/store/shared-actions';
import { makeLog } from 'shared/utils/log';
import { Trigger } from 'types';
import {
  createKit,
  createRealtimeReducerActions,
  getIntendedAssocVisitor,
  getIntendedVisitor,
} from './kit';
import { PeerStreamChange, sendEndCall, startMediaStream, stopMediaStream } from '.';
import { selectActiveVisitor } from 'shared/store/shared-selectors';

const log = makeLog('call-kit');

type PA<T> = PayloadAction<T>;

/**
 * Call start flow rough outline
 * 1) Reducer sets call establishing state based on isCallWanted
 * 2) When rt visitor state reflects the active call state, consider call active
 * 3) Once call is active, start the outgoing media stream
 */

/****************************************************
 *
 *                 Reducers
 *
 ****************************************************/

const reducers = {
  outgoingCameraStreamChanged: (state: RealtimeState, action: PA<string | null>) => {
    state.outgoingCameraStreamId = action.payload;
  },

  callRejected: (state: RealtimeState) => {
    state.callStatus = 'rejected';
    state.callStatusMsg = null;
    state.optimisticInteractions = {
      ...state.optimisticInteractions,
      isVideo: false,
      isTakeover: false,
      isDisplaydemo: false,
      isDisplayshare: false,
      callConversationId: 0,
      isVideoAccepted: false,
    };
  },

  allowedVisitorVideo: (state: RealtimeState, action: PA<boolean>) => {
    state.optimisticInteractions = {
      ...state.optimisticInteractions,
      isVisitorVideoAllowed: action.payload,
    };
  },

  incomingCameraStreamChanged: (state: RealtimeState, action: PA<PeerStreamChange>) => {
    const { streamId, isReceivingAudio, isReceivingVideo } = action.payload;
    state.incomingCameraStreamId = streamId;
    state.isReceivingAudio = isReceivingAudio;
    state.isReceivingVideo = isReceivingVideo;
  },
};
// Access actions before createSlice so we can reference them locally
const actions = createRealtimeReducerActions({ reducers });

export const callKit = createKit({
  reducers,
  extraReducers: builder => {
    // callWanted
    builder.addCase(callWanted.pending, (state, action) => {
      if (action.meta.arg) {
        state.callStatus = 'establishing';
        state.callStatusMsg = null;
        state.isCallWantedPending = true;
      } else {
        state.callStatus = 'off';
        state.callStatusMsg = null;
      }
    });
    builder.addCase(callWanted.fulfilled, (state, action) => {
      if (action.payload) {
        state.optimisticInteractions = {
          ...state.optimisticInteractions,
          callConversationId: action.payload,
          isVideo: true,
        };
        state.isCallWantedPending = false;
      } else {
        // End all interactions on call end
        state.optimisticInteractions = {
          ...state.optimisticInteractions,
          isVideo: false,
          isTakeover: false,
          isDisplaydemo: false,
          isDisplayshare: false,
          isVideoAccepted: false,
        };

        // if the action went from true to false, then turn off the call
        // this means that after the media device got its stream, the
        // action found that FE no longer was associated with a visitor
        if (action.meta.arg && !action.payload) {
          state.isCallWantedPending = false;
          state.callStatus = 'off';
        }
      }
    });
    builder.addCase(callWanted.rejected, (state, action) => {
      const err = action.error;
      // consider the call failed if there is a problem here
      const genericMsg = `Encountered an error attempting to start outgoing media stream`;
      log.error(genericMsg, undefined, err as Error);
      state.callStatus = 'failed';
      state.callStatusMsg = err.message || genericMsg;
      state.isCallWantedPending = false;
    });
  },

  /****************************************************
   *
   *               Handle triggers
   *
   ****************************************************/
  triggerThunkHelper: (trigger, _, dispatch) => {
    if (trigger === Trigger.STOP_VIDEO) {
      dispatch(callWanted({ callConversationId: 0, intentionSource: 'visitor' }));
    } else if (trigger === Trigger.STOP_VIDEO_DECLINE) {
      dispatch(
        callWanted({
          callConversationId: 0,
          intentionSource: 'visitor',
          reason: 'declined',
        }),
      );
    } else if (trigger === Trigger.STOP_VIDEO_MISSED) {
      dispatch(
        callWanted({
          callConversationId: 0,
          intentionSource: 'visitor',
          reason: 'missed',
        }),
      );
    } else if (trigger === Trigger.REJECT_CALL) {
      dispatch(actions.callRejected());
    }
  },

  /****************************************************
   *
   *         Visitor sync/update reducer helpers
   *
   ****************************************************/
  visitorUpdateReducer: state => {
    const assocVis = getIntendedAssocVisitor(state);
    const intendedVis = getIntendedVisitor(state);
    const isIntendedHelpedBySid = !!intendedVis?.helpedBySid;

    if (assocVis && assocVis.isVideo) {
      if (state.callStatus !== 'active') {
        if (assocVis.isPageVisible) {
          state.callStatus = 'active';
        } else {
          state.callStatus = 'establishing';
        }
      }
    } else if (
      isIntendedHelpedBySid &&
      !state.optimisticInteractions.isVideo &&
      !state.isCallWantedPending
    ) {
      // Only fully reset if:
      // 1. we're also not trying to start video (or a ?call)
      // 2. and the visitor we want to be associated with has another agent's sid (no sid means we may re-associate)
      state.callStatus = 'off';
    }
  },

  /****************************************************
   *
   *               Kit hook
   *
   ****************************************************/
  useKitHook: () => {
    const activeVisitor = useAppSelector(selectActiveVisitor);
    const { isConnectedToPeer, callStatus, outgoingCameraStreamId } = useAppSelector(
      s => s.realtime,
    );

    const prevCallStatus = usePrevious(callStatus);
    useEffect(
      function sendEndCallEffect() {
        if (prevCallStatus === 'active' && ['off', 'rejected'].includes(callStatus)) {
          log.debug(
            `Sending endCall because call status went from "${prevCallStatus}" -> "${callStatus}"`,
          );
          sendEndCall();
        }
      },
      [prevCallStatus, callStatus],
    );

    useEffect(
      function startMediaStreamEffect() {
        if (
          activeVisitor?.isVideo &&
          isConnectedToPeer &&
          callStatus === 'active' &&
          !outgoingCameraStreamId
        ) {
          log.debug('Re-establishing media stream for peer');
          startMediaStream();
        }
      },
      [activeVisitor?.isVideo, isConnectedToPeer, callStatus, outgoingCameraStreamId],
    );

    useEffect(
      function stopMediaStreamEffect() {
        if (outgoingCameraStreamId && !['establishing', 'active'].includes(callStatus)) {
          log.debug(`Outgoing stream will be closed because we are not on a call`, {
            outgoingCameraStreamId,
          });
          stopMediaStream();
        }
      },
      [outgoingCameraStreamId, callStatus],
    );
  },
});
